/**
 * Change 19 §3 — *"provide schools with complete financial and revenue-related
 * metrics."*
 *
 * 🔴 THE SCREEN THIS REPLACES DREW A REVENUE CURVE THAT WAS A HARDCODED SVG
 * PATH. Two `<path d="M0,90 Q70,75 …">` literals, rendered whenever total
 * revenue was greater than zero, under an axis labelled *Day 1 … Day 30* and a
 * period selector wired to nothing. It rose pleasantly in every deployment, on
 * every dataset, in every month — including a month where collections fell. A
 * fabricated number on an operator screen is bad; a fabricated *trend* on a
 * financial one is worse, because nobody checks the shape of a line against
 * arithmetic they could do themselves.
 *
 * 🔴 EVERY FIGURE HERE COMES FROM ONE OF TWO SOURCES AND THEY ANSWER DIFFERENT
 * QUESTIONS. Mixing them is the classic finance-dashboard error:
 *
 *   - the **payments ledger** answers *"what was actually collected, and when"*.
 *     It is history and it does not move.
 *   - the **subscriptions** answer *"what is being charged from now on"*. It is
 *     a forecast, it moves with enrolment, and it is not revenue.
 *
 * MRR is the second. "Total collected" is the first. A screen that adds them
 * together, or labels one as the other, is telling somebody they have money they
 * do not have.
 *
 * 🔴 INTEGER PAISE THROUGHOUT — `billing.ts`'s rule, and the reason is the same
 * one: `Number('19.99') * 100` is 1998.9999999999998.
 */

export interface PaymentLike {
  amountPaise: number;
  status: string;
  /** When it was actually paid. `createdAt` is when the row appeared. */
  paidAt: string | null;
  createdAt: string;
}

export interface SubscriptionLike {
  status: string;
  netChargePaise: number | null;
  chargePaise: number | null;
  seats: number;
  plan: { name: string; period: string } | null;
}

/* ───────────────────────────────────────────────── what was collected ── */

export interface RevenuePoint {
  /** `2026-08` — sortable, and the label is derived rather than stored. */
  month: string;
  paise: number;
}

/**
 * ⚠️ `paidAt` WHERE THERE IS ONE, FALLING BACK TO `createdAt`. A payment sits in
 * the month the money moved, not the month somebody typed it in — an NEFT
 * recorded a week late belongs to the week it arrived, or every month-end
 * reconciliation is off by whatever the operator's backlog was.
 */
const monthOf = (payment: PaymentLike): string =>
  (payment.paidAt ?? payment.createdAt).slice(0, 7);

/** `2026-08` → `Aug` / `Aug 26` when the year is not the current one. */
export function monthLabel(month: string, now: Date): string {
  const [year, m] = month.split("-");
  const date = new Date(Date.UTC(Number(year), Number(m) - 1, 1));
  const short = date.toLocaleDateString("en-GB", { month: "short", timeZone: "UTC" });
  return Number(year) === now.getUTCFullYear() ? short : `${short} ${year.slice(2)}`;
}

/**
 * Collections per month, oldest first, with empty months present as zero.
 *
 * 🔴 THE EMPTY MONTHS ARE THE POINT. A series built only from months that have
 * payments draws a line straight over a month where nothing was collected — the
 * single most important shape on this chart, and the one a `groupBy` silently
 * removes.
 *
 * ⚠️ ONLY `PAID` COUNTS. A pending payment is an intention and a failed one is
 * an absence; both belong in the payments table and neither is revenue.
 */
export function monthlyRevenue(
  payments: readonly PaymentLike[],
  now: Date,
  months = 12,
): RevenuePoint[] {
  const byMonth = new Map<string, number>();
  for (const payment of payments) {
    if (payment.status !== "PAID") continue;
    const key = monthOf(payment);
    byMonth.set(key, (byMonth.get(key) ?? 0) + payment.amountPaise);
  }

  const series: RevenuePoint[] = [];
  for (let back = months - 1; back >= 0; back -= 1) {
    const d = new Date(Date.UTC(now.getUTCFullYear(), now.getUTCMonth() - back, 1));
    const key = `${String(d.getUTCFullYear())}-${String(d.getUTCMonth() + 1).padStart(2, "0")}`;
    series.push({ month: key, paise: byMonth.get(key) ?? 0 });
  }
  return series;
}

/** What was collected in the calendar month `now` falls in. */
export function collectedThisMonth(payments: readonly PaymentLike[], now: Date): number {
  const key = `${String(now.getUTCFullYear())}-${String(now.getUTCMonth() + 1).padStart(2, "0")}`;
  return payments
    .filter((p) => p.status === "PAID" && monthOf(p) === key)
    .reduce((sum, p) => sum + p.amountPaise, 0);
}

/** Everything ever collected. History, and it does not move. */
export function collectedEver(payments: readonly PaymentLike[]): number {
  return payments
    .filter((p) => p.status === "PAID")
    .reduce((sum, p) => sum + p.amountPaise, 0);
}

/* ─────────────────────────────────────────────── what is being charged ── */

/**
 * How many months one billing period covers.
 *
 * 🔴 `QUARTERLY` WAS SILENTLY TREATED AS MONTHLY. The old code branched on
 * `period === 'YEARLY'` and put everything else in the monthly bucket — so a
 * quarterly plan contributed three times its true MRR, and `BillingPeriod`
 * has had a `QUARTERLY` member the whole time. Exhaustive here, and unknown
 * periods are refused rather than guessed.
 */
export function monthsIn(period: string): number | null {
  if (period === "MONTHLY") return 1;
  if (period === "QUARTERLY") return 3;
  if (period === "YEARLY") return 12;
  return null;
}

export interface RecurringRevenue {
  mrrPaise: number;
  arrPaise: number;
  /** Average revenue per paying school, per month. `null` when none is paying. */
  arpuPaise: number | null;
  /** Per enrolled child, per month, across paying schools. `null` when no seats. */
  perChildPaise: number | null;
  payingSchools: number;
  /**
   * 🔴 SUBSCRIPTIONS WHOSE PERIOD THIS FILE COULD NOT READ, COUNTED RATHER THAN
   * DROPPED. A plan on a period nobody taught this function about would
   * otherwise vanish from MRR with nothing anywhere saying so — the screen shows
   * this number when it is not zero, which is how the omission becomes visible
   * instead of becoming a wrong total.
   */
  unreadable: number;
}

/**
 * What the platform is charging per month, normalised.
 *
 * 🔴 IT IS NOT REVENUE AND MUST NOT BE LABELLED AS SUCH. It is what live
 * subscriptions come to at today's enrolment — a forecast that moves whenever a
 * child is enrolled. `collectedEver` is the money.
 *
 * ⚠️ `netChargePaise` — AFTER THE DISCOUNT, NOT BEFORE. A school on a coupon
 * pays the net figure, and counting the list price as recurring revenue is how a
 * platform forecasts income it has already agreed not to collect.
 *
 * ⚠️ TRIALS ARE EXCLUDED. A free trial charges nothing this month; including it
 * at its would-be rate is counting a hope.
 */
export function recurringRevenue(subs: readonly SubscriptionLike[]): RecurringRevenue {
  let mrrPaise = 0;
  let payingSchools = 0;
  let seats = 0;
  let unreadable = 0;

  for (const sub of subs) {
    if (sub.status !== "ACTIVE") continue;
    const charge = sub.netChargePaise ?? sub.chargePaise;
    if (charge === null || charge <= 0) continue;

    const months = sub.plan === null ? null : monthsIn(sub.plan.period);
    if (months === null) {
      unreadable += 1;
      continue;
    }

    mrrPaise += Math.round(charge / months);
    payingSchools += 1;
    seats += sub.seats;
  }

  return {
    mrrPaise,
    arrPaise: mrrPaise * 12,
    arpuPaise: payingSchools === 0 ? null : Math.round(mrrPaise / payingSchools),
    perChildPaise: seats === 0 ? null : Math.round(mrrPaise / seats),
    payingSchools,
    unreadable,
  };
}

/**
 * What is owed and not paid.
 *
 * 🔴 IT IS COUNTED FROM SUBSCRIPTION STATUS, NOT FROM UNPAID PAYMENT ROWS. A
 * school that has never been invoiced has no payment row at all, so a sum over
 * `status !== 'PAID'` reports zero for exactly the schools furthest behind.
 * `PAST_DUE` and `GRACE` are the states the billing service puts a school in
 * when a period ends unpaid.
 */
export interface Outstanding {
  schools: number;
  paise: number;
}

export function outstanding(subs: readonly SubscriptionLike[]): Outstanding {
  const behind = subs.filter((s) => s.status === "PAST_DUE" || s.status === "GRACE");
  return {
    schools: behind.length,
    paise: behind.reduce((sum, s) => sum + (s.netChargePaise ?? s.chargePaise ?? 0), 0),
  };
}

/**
 * Recurring revenue by plan, normalised to a month.
 *
 * 🔴 NORMALISED, WHICH THE OLD "REVENUE BY BILLING CYCLE" PANEL WAS NOT. It
 * added a yearly charge to a monthly one and showed the two as a share of a
 * total — so one annual school looked like twelve monthly ones, and a plan's
 * share of revenue moved when a school changed period without paying a rupee
 * more.
 */
export interface PlanRevenue {
  name: string;
  mrrPaise: number;
  schools: number;
  /** Share of MRR, 0-100, rounded to one place. */
  share: number;
}

export function revenueByPlan(subs: readonly SubscriptionLike[]): PlanRevenue[] {
  const byPlan = new Map<string, { mrrPaise: number; schools: number }>();

  for (const sub of subs) {
    if (sub.status !== "ACTIVE" || sub.plan === null) continue;
    const charge = sub.netChargePaise ?? sub.chargePaise;
    const months = monthsIn(sub.plan.period);
    if (charge === null || charge <= 0 || months === null) continue;

    const row = byPlan.get(sub.plan.name) ?? { mrrPaise: 0, schools: 0 };
    row.mrrPaise += Math.round(charge / months);
    row.schools += 1;
    byPlan.set(sub.plan.name, row);
  }

  const total = [...byPlan.values()].reduce((sum, r) => sum + r.mrrPaise, 0);
  return [...byPlan.entries()]
    .map(([name, row]) => ({
      name,
      mrrPaise: row.mrrPaise,
      schools: row.schools,
      share: total === 0 ? 0 : Math.round((row.mrrPaise / total) * 1000) / 10,
    }))
    .sort((a, b) => b.mrrPaise - a.mrrPaise || a.name.localeCompare(b.name));
}

/* ──────────────────────────────────────────────────────── the drawing ── */

/**
 * A series → an SVG polyline, in a 0-100 box.
 *
 * 🔴 IT EXISTS SO THE CHART CANNOT BE DRAWN WITHOUT THE DATA. The path it
 * replaces was a string literal; a function that takes the series and returns
 * the geometry is one that renders a flat line when nothing was collected,
 * because that is what the arithmetic says.
 *
 * ⚠️ THE SCALE IS ALWAYS FROM ZERO. A chart auto-scaled to its own minimum turns
 * a 2% dip into a cliff, which on a revenue screen is the difference between a
 * quiet month and a phone call to an investor.
 */
export function seriesPath(series: readonly RevenuePoint[], width = 100, height = 100): string {
  if (series.length === 0) return "";
  const peak = Math.max(...series.map((p) => p.paise), 1);
  const step = series.length === 1 ? 0 : width / (series.length - 1);

  return series
    .map((point, i) => {
      const x = Math.round(i * step * 100) / 100;
      const y = Math.round((height - (point.paise / peak) * height) * 100) / 100;
      return `${i === 0 ? "M" : "L"}${String(x)},${String(y)}`;
    })
    .join(" ");
}

/** The same series as a closed shape, for the fill under the line. */
export function seriesArea(series: readonly RevenuePoint[], width = 100, height = 100): string {
  const line = seriesPath(series, width, height);
  if (line === "") return "";
  return `${line} L${String(width)},${String(height)} L0,${String(height)} Z`;
}
